import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Contact from "@/services/contact/contact";

export type ApiPhoneNumberType = "DIRECT" | "MOBILE" | "WORK" | "HOME" | "OTHER";

export interface EditContactPhoneNumberPayload {
  contactId: string;
  phoneNumber: string;
  oldPhoneNumber: string;
  phoneNumberType?: ApiPhoneNumberType;
  oldPhoneNumberType?: ApiPhoneNumberType;
  routeType?: "DIRECT" | "MOBILE" | "IVR" | "GATEKEEPER";
  ivrExtension?: string;
  gatekeeperName?: string;
  isValid?: boolean | null;
}

/**
 * Mutation composable for editing a contact phone number.
 * Uses the existing contact service endpoint and invalidates contact-related queries.
 */
export function useEditContactPhoneNumber() {
  const queryClient = useQueryClient();

  const editMutation = useMutation({
    mutationFn: async (payload: EditContactPhoneNumberPayload) => {
      // The API endpoint accepts a payload specific to phone-number edits, which is not modeled
      // by the generic EditContact DTO. We keep it strongly typed here and cast only at the callsite.
      const result = await Contact.editContactPhoneNumber(payload as any);

      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to edit phone number");
      }

      return result.data.data;
    },
    onSuccess: () => {
      // Broad invalidation is safest because query keys for contact are parameterized in several ways.
      queryClient.invalidateQueries({ queryKey: ["contact"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["buckets"] });
      queryClient.invalidateQueries({ queryKey: ["bucketCounts"] });
    },
  });

  return {
    editContactPhoneNumber: editMutation.mutateAsync,
    isEditing: editMutation.isPending,
    editError: editMutation.error,
  };
}
